import React from 'react';

import { cssMap, cx } from '@compiled/react';

import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	type HeaderElementType,
	isHeaderElement,
	type ParsedElementType,
	type SectionElementType,
} from '../types';

import { FoldableSection } from './foldable-section';
import { OneColumnSection } from './one-column-section';
import { TwoColumnSection } from './two-column-section';
import { TwoRowSection } from './two-row-section';

const styles = cssMap({
	overviewStyle: {
		borderRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
		marginBottom: token('space.200'),
	},
	sectionWrapperStyles: {
		marginTop: token('space.300'),
	},
});

export const LuminaSection = ({
	section,
	defaultFoldedState,
}: {
	defaultFoldedState: 'collapsed' | 'expanded' | undefined;
	section: SectionElementType;
}): React.JSX.Element | null => {
	const layout = section.attributes?.layout || 'one-column';
	const isOverview = section.attributes?.overview === '1';
	const isFoldable = section.attributes?.hasOwnProperty('folded');

	let header: HeaderElementType | undefined;
	let content: ParsedElementType[] | undefined;
	if (Array.isArray(section.children)) {
		const headerElement = section.children.filter((element) => isHeaderElement(element));
		if (headerElement.length) {
			header = headerElement[0] as HeaderElementType;
		}
		content = section.children?.filter((element) => !isHeaderElement(element));
	}

	if (!content || (content && !content.length)) {
		return null;
	}

	const renderContent = () => {
		switch (layout) {
			case 'one-column':
				return <OneColumnSection elements={content} isFoldable={isFoldable} />;
			case 'two-columns':
				return (
					<TwoColumnSection
						elements={content}
						key={section.attributes?.n}
						isFoldable={isFoldable}
					/>
				);
			case 'two-rows':
				return (
					<TwoRowSection elements={content} key={section.attributes?.n} isFoldable={isFoldable} />
				);
		}
	};

	if (defaultFoldedState) {
		return (
			<FoldableSection defaultFoldedState={defaultFoldedState} header={header} content={content}>
				{renderContent()}
			</FoldableSection>
		);
	}

	return (
		<Stack
			space="space.100"
			alignInline="start"
			xcss={cx(isOverview && styles.overviewStyle)}
			testId="dynamic-layout-section"
			data-layout={layout}
			data-overview={isOverview}
		>
			{header && header.attributes?.show !== 'false' && content && (
				<Inline space="space.100" alignBlock="center" xcss={styles.sectionWrapperStyles}>
					<Text weight="bold">{header && (header.children as string)}</Text>
				</Inline>
			)}
			{renderContent()}
		</Stack>
	);
};
